import { cast, flow, getParent, getRoot, Instance, types } from 'mobx-state-tree';
import { CmsApi } from 'service/axios-api';
import { IRootStore } from 'store/root';
import { IFeedStore } from './index';
import { NewsModel } from './news-model';
import { ToyCommuStore } from './toy-commu';

const NewsStore = types
    .model('NewsStore', {
        locales: types.optional(types.string, ''),
        Categories: types.array(types.string),
        Category: types.optional(types.string, 'All'), // current selected one
        // Total: types.optional(types.number, 0),
        listModel: types.array(NewsModel),
        // fetchedAll: types.optional(types.boolean, false),
        limit: types.optional(types.number, 0),
        currentIndex: types.maybe(types.number), // current detail news one
        toyCommuStore: types.optional(ToyCommuStore, {}),
    })
    .views((self) => ({
        get root(): IRootStore {
            return getRoot<IRootStore>(self);
        },
        get productId(): string {
            return getParent<IFeedStore>(self).productId;
        },
        get useToyCommu() {
            return ['59789', '59794'].includes(this.productId);
        },
        get list() {
            if (this.useToyCommu) {
                return self.toyCommuStore.list;
            }

            return self.listModel;
        },
    }))
    .views((self) => {
        const views = {
            get filtered() {
                if (self.useToyCommu) {
                    return self.toyCommuStore.filtered;
                }

                if (self.Category === 'All') return self.list;
                return self.list.filter((x) => x.Category === self.Category);
            },
            get currentNews() {
                if (self.currentIndex === undefined) return undefined;
                if (self.useToyCommu) {
                    return self.toyCommuStore.currentNews;
                }

                return self.list[self.currentIndex];
            },
            get prev() {
                if (self.currentIndex === undefined || self.currentIndex < 1) return undefined;
                if (self.useToyCommu) {
                    return self.toyCommuStore.prev;
                }

                return self.list[self.currentIndex - 1];
            },
            get next() {
                if (self.currentIndex === undefined || self.currentIndex > self.list.length - 2) return undefined;
                if (self.useToyCommu) {
                    return self.toyCommuStore.next;
                }

                return self.list[self.currentIndex + 1];
            },
        };
        return views;
    })
    .views((self) => ({
        get limited() {
            return self.filtered.slice(0, self.limit);
        },
    }))
    .views((self) => ({
        get locale(): string {
            return self.locales.indexOf(self.root.systemInfo.locale) < 0 ? self.root.systemInfo.defaultLocale : self.root.systemInfo.locale;
        },
    }))
    .actions((self) => {
        const actions = {
            fetchLocales: flow(function* () {
                self.locales = yield CmsApi.get(`/news/regions/{regionCode}/${self.productId}/locales`).catch(() => '');
            }),
            fetchList: flow(function* () {
                const data = yield CmsApi.get(`/news/regions/{regionCode}/${self.productId}/${self.locale}/list.json`).catch(() => []);
                self.listModel = cast(data.slice(0, 100));
            }),
            isDefaultThumbnail(url: string) {
                return url.endsWith('forum_NEXON.jpg');
            },
        };
        return actions;
    })
    .actions((self) => {
        const actions = {
            fetch: flow(function* () {
                if (!self.locales) {
                    yield self.fetchLocales();
                    if (!self.locales) return; // no support for news
                }
                self.limit = 15;
                if (self.useToyCommu) {
                    self.Categories = cast(self.toyCommuStore.getCategories());
                    self.toyCommuStore.fetchList();
                } else {
                    const data = yield CmsApi.get(`/news/regions/{regionCode}/${self.productId}/${self.locale}/recent.json`);
                    self.Categories = cast(['All'].concat(data.Categories));
                    self.fetchList();
                }
            }),
            fetchMore() {
                self.limit += 5;
            },
            setCategory(cat: string) {
                self.Category = cat;
            },
            setCategories(cats: string[]) {
                self.Categories = cast(cats);
            },
            setCurrent(newsId: number | undefined) {
                if (self.useToyCommu) {
                    self.toyCommuStore.setCurrent(newsId);
                } else {
                    const index = self.list.findIndex((x) => x.Id === newsId);
                    this.setCurrentIndex(index >= 0 ? index : undefined);
                }
            },
            goNext() {
                if (self.currentIndex !== undefined && self.next) {
                    this.setCurrentIndex(self.currentIndex + 1);
                }
            },
            goPrev() {
                if (self.currentIndex !== undefined && self.prev) {
                    this.setCurrentIndex(self.currentIndex - 1);
                }
            },
            setCurrentIndex(idx: number | undefined) {
                self.currentIndex = idx;
            },
        };
        return actions;
    });

export type INewsStore = Instance<typeof NewsStore>;
export type INewsModel = Instance<typeof NewsModel>;

export default NewsStore;
